import { ApiClientError, TacticApiClient } from "@gettactic/api";
import { getHeaderSlug } from "@gettactic/api/src/endpoints/base";
import { useQuery } from "@tanstack/react-query";
import { replaceLocationHref } from "../navigation";
import { getBaseUrl } from "../strings";
import { getVercelSlug, isVercelDeployment } from "../vercel/vercelPreview";

// TODO: remove after testing
export const USE_ONLY_LOCAL_STORAGE_FOR_DS = true;

export interface UseGetAuthenticatedUserOptions {
  allowRedirect?: boolean;
  /** When false, all queries are disabled and the hook is a no-op. Defaults to true. */
  enabled?: boolean;
}

export function useGetAuthenticatedUser(
  api: TacticApiClient,
  embedded = false,
  options?: UseGetAuthenticatedUserOptions
) {
  const hookEnabled = options?.enabled !== false;

  const basicUser = useQuery({
    queryKey: ["users.me"],

    queryFn: async () => {
      try {
        const res = await api.client.users.me();
        return res.result;
      } catch (e) {
        if (
          options?.allowRedirect !== false &&
          e instanceof ApiClientError &&
          e.response.status === 401
        ) {
          replaceLocationHref(
            `${process.env.NEXT_PUBLIC_TACTIC_API_URL}/auth/login?returnUrl=${encodeURIComponent(
              `${process.env.NEXT_PUBLIC_TACTIC_DOMAIN_AND_PORT}/dashboard`
            )}`
          );
          return null;
        }
        throw e;
      }
    },

    // 5 minutes,
    staleTime: 5000 * 60,
    enabled: hookEnabled
  });

  const userReady = !!basicUser.data;

  const organizations = useQuery({
    queryKey: ["users.organizations"],

    queryFn: async () => {
      const res = await api.client.users.organizations();
      return res.result;
    },

    // 5 minutes
    staleTime: 5000 * 60,

    enabled: hookEnabled && userReady
  });

  let slug = "";
  if (
    !organizations.isLoading &&
    organizations.data?.elements &&
    organizations.data?.elements.length > 0
  ) {
    const defaultOrg = organizations.data.elements.filter(
      (organization) =>
        organization.id === basicUser.data?.default_organization_id
    )[0];
    // Set the slug to the user's default organization, otherwise the slug of the first organization they're a part of
    slug = defaultOrg
      ? defaultOrg.slug
      : (organizations.data.elements[0].slug ?? "");

    // If we're in the browser context & there is an intended slug, it has priority.
    // On Vercel preview deployments there is no per-org subdomain, so the intended
    // slug comes from the value the tester saved in localStorage instead of the host.
    // Vercel previews always call this hook with allowRedirect: false (that flag only
    // suppresses subdomain redirects, not slug resolution), so the override must be
    // honored regardless of it — otherwise the user's default org silently wins.
    if (
      typeof window !== "undefined" &&
      (isVercelDeployment() || options?.allowRedirect !== false)
    ) {
      const intendedSlug = isVercelDeployment()
        ? (getVercelSlug() ?? "")
        : window.location.host.split(".")[0];
      const resolvedSubdomain = getHeaderSlug(intendedSlug);
      const matchingOrg = organizations.data.elements.find(
        (org) => org.slug === intendedSlug || org.slug === resolvedSubdomain
      );
      if (matchingOrg) {
        slug = matchingOrg.slug;
      }
    }
  }

  // If we have our user, but no slug exists then we need to create an organization
  if (
    options?.allowRedirect !== false &&
    !basicUser.isLoading &&
    !organizations.isLoading &&
    (!organizations.data ||
      !organizations.data?.elements ||
      organizations.data?.elements.length <= 0) &&
    basicUser &&
    basicUser.data &&
    !slug &&
    !embedded
  ) {
    replaceLocationHref(
      `${process.env.NEXT_PUBLIC_TACTIC_DOMAIN_AND_PORT}/setup`
    );
  }

  // Slug is a required on the api-client for the remaining queries
  if (slug) {
    api.updateSlug(slug);
  }

  // If the slug doesn't match, redirect to the proper subdomain
  // Allow slug aliases (e.g. redbull-new → redbull) to stay on their subdomain.
  // Vercel previews have no per-org subdomain, so never redirect away from them.
  if (
    options?.allowRedirect !== false &&
    slug &&
    !isVercelDeployment() &&
    window.location.host.split(".")[0] !== slug &&
    getHeaderSlug(window.location.host.split(".")[0]) !== slug &&
    !embedded
  ) {
    replaceLocationHref(`${getBaseUrl(slug)}/dashboard`);
  }

  const user = useQuery({
    queryKey: ["organizations.getOrgMe"],

    queryFn: async () => {
      const res = await api.client.organizations.getOrgMe();
      return res.result;
    },

    enabled: hookEnabled && !!slug,

    // 5 minutes
    staleTime: 5000 * 60
  });

  const organization = useQuery({
    queryKey: ["organizations.get"],

    queryFn: async () => {
      const res = await api.client.organizations.get(slug);
      return res.result;
    },

    // Don't run until the slug exists
    enabled: hookEnabled && !!slug,

    // 5 minutes
    staleTime: 5000 * 60
  });

  const offices = useQuery({
    queryKey: ["organizations.offices"],

    queryFn: async () => {
      const res = await api.client.organizations.offices();
      return res.result;
    },

    enabled: hookEnabled && !!slug,

    // 5 minutes
    staleTime: 5000 * 60
  });

  const isLoading =
    basicUser.isLoading ||
    organizations.isLoading ||
    user.isLoading ||
    organization.isLoading ||
    offices.isLoading;

  const authenticatedUser =
    !!user.data && !!organization.data && !!organizations.data && !!offices.data
      ? api.client.organizations.me(
          user.data,
          organization.data,
          organizations.data,
          offices.data
            ? offices.data
            : { byId: {}, office: null, officeId: null, offices: [] }
        )
      : null;

  return { user: authenticatedUser, isLoading };
}
